import React, { Dispatch, FC, SetStateAction, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FiEdit } from "react-icons/fi";
import { toast } from "react-toastify";
import { useDebounce } from "usehooks-ts";

import Button from "../../../../../../components/button/Button";
import { Card } from "../../../../../../components/card/Card";
import CustomSelect from "../../../../../../components/select/Select";
import {
  getInitials,
  getInitialsFromFullName,
} from "../../../../../../constants/constants";
import { WorkDetailsModel } from "../../../../../../factories/work/model/work-details.model";
import { useAppSelector } from "../../../../../../redux/redux";
import { getLinkedFirm } from "../../../../../../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../../../../../../slices/ContactsSlice";
import { useGetUsersQuery } from "../../../../../../slices/UserSlice";
import {
  useUpdateWorkAssigneesMutation,
  useUpdateWorkMutation,
  useUpdateWorkRepeatsMutation,
} from "../../../../../../slices/WorkSlice";
import { useGetWorkTypesQuery } from "../../../../../../slices/WorkTypeSlice";
import {
  repeats,
  repeatTypeEnumNever,
  SelectOptionType,
} from "../../../../../../types/types";
import { RecurringScheduleModal } from "../../../repeats/RecurringScheduleModal";
import { Repeat } from "../../../repeats/Repeats";

type Props = {
  work: WorkDetailsModel;
  setIsEdit: Dispatch<SetStateAction<boolean>>;
  setIsOpenMessage: Dispatch<SetStateAction<boolean>>;
};

export type RepeatsData = {
  repeatOnId: string | null;
  repeat: string;
  repeatCount: string;
  days: string;
  repeatUntil: Date;
  generatedDate: string | null;
  dueDate: string;
  beforeStartDate: number;
  beforeStartDateValue: string;
};

type FormData = {
  customer: SelectOptionType;
  workType: SelectOptionType;
  user: SelectOptionType[];
};

const MAX_ASSIGNEES = 5;

const getDefaultAssignees = (work: WorkDetailsModel): SelectOptionType[] => {
  if (work.jobAssignees?.length) {
    return work.jobAssignees.map((assignee) => ({
      label:
        assignee.assignedUser.userProfile.firstName +
        " " +
        assignee.assignedUser.userProfile.lastName,
      value: assignee.assignedUserId,
      avatar: getInitials(
        assignee.assignedUser.userProfile.firstName,
        assignee.assignedUser.userProfile.lastName,
      ),
      avatarColor: "blue",
    }));
  }

  return [
    {
      label:
        work?.assignedUser?.userProfile?.firstName +
        " " +
        work?.assignedUser?.userProfile?.lastName,
      value: work?.assignedUserId,
      avatar: getInitials(
        work?.assignedUser?.userProfile?.firstName,
        work?.assignedUser?.userProfile?.lastName,
      ),
      avatarColor: "blue",
    },
  ];
};

export const EditWorkDetails: FC<Props> = ({
  work,
  setIsEdit,
  setIsOpenMessage,
}) => {
  const { user } = useAppSelector((state) => state.appReducer);
  const [searchContacts, setSearchContacts] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const searchContactsDebounce = useDebounce(searchContacts, 50);
  const [repeatsData, setRepeatsData] = useState<RepeatsData>({
    days: work?.beforeDays.toString(),
    repeat: work?.repeatTypeEnumId,
    repeatCount: (work?.repeatValue || 1).toString(),
    repeatOnId: work?.repeatOnId || null,
    generatedDate: null,
    repeatUntil: work?.repeatUntil ? new Date(work?.repeatUntil) : new Date(),
    dueDate: "1",
    beforeStartDate: work.beforeStartDate,
    beforeStartDateValue: work.beforeStartDateValue,
  });

  const { data: contacts, isLoading: isLoadingContacts } =
    useGetSearchCustomersQuery({
      searchQuery: searchContactsDebounce || "ab",
      orgId: getLinkedFirm()?.orgId || "",
      userId: user.id,
      isActive: true,
    });
  const { data: workTypes, isLoading: isLoadingWorkTypes } =
    useGetWorkTypesQuery({
      orgId: getLinkedFirm()?.orgId || "",
    });
  const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery(
    getLinkedFirm()?.orgId || "",
  );

  const [updateDetails, { isLoading: isLoadingUpdate }] =
    useUpdateWorkMutation();
  const [updateAssignee, { isLoading: isLoadingUpdateAssignee }] =
    useUpdateWorkAssigneesMutation();
  const [updateWorkRepeats] = useUpdateWorkRepeatsMutation();

  const { handleSubmit, control } = useForm<FormData>({
    defaultValues: {
      customer: {
        label: work?.customer?.name,
        value: work?.customerId,
        secondValue: work?.customer?.businessContactId || "",
        avatar: getInitialsFromFullName(work?.customer?.name),
        avatarColor: work?.customer?.businessContactId ? "purple" : "blue",
      },
      user: getDefaultAssignees(work),
      workType: { value: work?.serviceId, label: work?.service?.name },
    },
  });

  const onSubmit = async (data: FormData) => {
    if (isLoadingUpdate || isLoadingUpdateAssignee || isOpen) {
      return;
    }

    try {
      await updateDetails({
        workId: work?.id,
        workName: work?.name,
        serviceId: data?.workType?.value,
        taskTemplateId: work?.templateId,
        userId: work?.assignedUserId,
        customerId: data?.customer?.value,
        businessContactId: data?.customer?.secondValue || null,
        isReassigned: work?.isReassigned,
        assignedByUserId: work?.assignedByUserId || user.id,
      }).unwrap();

      await updateAssignee({
        jobId: work.id,
        assignedByUserId: work?.assignedByUserId || user.id,
        userIds: data.user.map((assignee) => assignee.value),
      }).unwrap();

      toast.success("Work updated successfully");
      setIsEdit(false);
    } catch {
      toast.error("Failed to update work");
    }
  };

  const contactsOptions = useMemo<SelectOptionType[]>(() => {
    return (
      contacts?.map((contact) => ({
        value: contact?.customerId,
        secondValue: contact?.businessContactId || "",
        label: `${contact.customerName}`,
        avatar: getInitialsFromFullName(contact?.customerName),
        avatarColor: contact.businessContactId ? "purple" : "blue",
      })) ?? []
    );
  }, [contacts]);

  const workTypeOptions = useMemo<SelectOptionType[]>(() => {
    return (
      workTypes?.map((workType) => ({
        value: workType?.id,
        label: workType?.name,
      })) ?? []
    );
  }, [workTypes]);

  const userOptions = useMemo<SelectOptionType[]>(() => {
    return (
      users?.map((assignee) => ({
        value: assignee?.id,
        label:
          assignee?.userProfile?.firstName +
          " " +
          assignee?.userProfile?.lastName,
        avatar: getInitials(
          assignee?.userProfile?.firstName,
          assignee?.userProfile?.lastName,
        ),
        type: "custom",
        avatarCustomColor: assignee?.userProfile?.color || "blue",
      })) ?? []
    );
  }, [users]);

  const isSaving = isLoadingUpdate || isLoadingUpdateAssignee;

  return (
    <Card extraClasses={"shadow-box"}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <div
            className={
              "flex justify-between border-b border-gray-300 px-6 py-4"
            }>
            <div className={"text-base font-semibold"}>Edit details</div>
          </div>
          <div className={"flex flex-col gap-8 px-6 py-4 text-base"}>
            <div className={"flex flex-col gap-2"}>
              <div className={"text-sm text-gray-500"}>Client</div>
              <Controller
                name={"customer"}
                control={control}
                rules={{
                  required: "This field is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <CustomSelect
                    options={contactsOptions}
                    value={value}
                    onChange={(newValue) => {
                      const newContact = newValue as SelectOptionType;
                      onChange(newContact);
                    }}
                    withMenu
                    inputValue={searchContacts}
                    onChangeInput={setSearchContacts}
                    isLoading={isLoadingContacts}
                    selectStyle={{ fullWidth: true }}
                  />
                )}
              />
            </div>
            <div className={"flex flex-col gap-2"}>
              <div className={"text-sm text-gray-500"}>Work Type</div>
              <Controller
                name={"workType"}
                control={control}
                rules={{
                  required: "This field is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <CustomSelect
                    options={workTypeOptions}
                    value={value}
                    withMenu
                    onChange={(newValue) => {
                      const newWorkType = newValue as SelectOptionType;
                      onChange(newWorkType);
                    }}
                    isLoading={isLoadingWorkTypes}
                    selectStyle={{ fullWidth: true }}
                  />
                )}
              />
            </div>
            <div className={"flex flex-col gap-2"}>
              <div className={"text-base text-gray-500"}>Assignee</div>
              <Controller
                name={"user"}
                control={control}
                rules={{
                  required: "This field is required",
                  validate: (value) => {
                    if (value.length === 0) {
                      return "This field is required";
                    }

                    return (
                      value.length <= MAX_ASSIGNEES ||
                      `Select up to ${MAX_ASSIGNEES} assignees`
                    );
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <CustomSelect
                    options={userOptions}
                    value={value}
                    withMenu
                    isMulti
                    onChange={(newValue) => {
                      const nextAssignees = newValue as SelectOptionType[];

                      if (nextAssignees.length <= MAX_ASSIGNEES) {
                        onChange(nextAssignees);
                      }
                    }}
                    isLoading={isLoadingUsers}
                    selectStyle={{ fullWidth: true, fitChips: true }}
                  />
                )}
              />
            </div>
            <div className={"flex flex-col gap-2"}>
              <div className={"text-base text-gray-500"}>Recurring</div>
              <div className={"flex justify-between"}>
                <div className={"text-sm font-semibold"}>
                  {repeats.find((item) => repeatsData?.repeat === item.id)
                    ?.name || "Never"}
                </div>
                <div
                  onClick={() => setIsOpen(true)}
                  className={"cursor-pointer"}>
                  <FiEdit size={"20px"} className={"text-gray-400"} />
                </div>
              </div>
            </div>
            <div className={"grid w-full grid-cols-[1fr_1fr] gap-2"}>
              <Button
                fullWidth
                label={"Cancel"}
                size={"custom"}
                colorType={"outline"}
                extraClasses={"btn-sm"}
                buttonType={"button"}
                onClick={() => setIsEdit(false)}
              />
              <Button
                fullWidth
                label={"Save"}
                size={"custom"}
                extraClasses={"btn-sm"}
                buttonType={"submit"}
                isLoading={isSaving}
                disabled={isSaving}
              />
            </div>
          </div>
        </div>
        {isOpen &&
          (!repeatsData?.repeat ||
            repeatsData.repeat.toLowerCase() === repeatTypeEnumNever.id) && (
            <RecurringScheduleModal
              work={work}
              onClose={() => setIsOpen(false)}
              onSave={(payload) => {
                setIsOpen(false);
                setRepeatsData((current) => ({
                  ...current,
                  repeat: payload.repeatTypeEnumId,
                }));
                updateWorkRepeats({ workId: work.id, ...payload })
                  .unwrap()
                  .then(() => toast.success("Recurring schedule started"))
                  .catch(() =>
                    toast.error(
                      "Couldn't start the recurring schedule. Please try again.",
                    ),
                  );
              }}
            />
          )}
        {isOpen &&
          !!repeatsData?.repeat &&
          repeatsData.repeat.toLowerCase() !== repeatTypeEnumNever.id && (
            <Repeat
              setIsOpenMessage={setIsOpenMessage}
              jobId={work.id}
              work={work}
              values={repeatsData}
              close={() => {
                setIsOpen(false);
              }}
              onSubmit={(data) => {
                setIsOpen(false);
                updateWorkRepeats({
                  workId: work?.id,
                  repeatTypeEnumId: data?.repeat,
                  beforeDays: Number(data?.days),
                  repeatOnId: data?.repeatOnId,
                  repeatValue: data?.repeatCount,
                  repeatUntil: data?.repeatUntil,
                  dueDate: data?.dueDate,
                  beforeStartDate: data.beforeStartDate,
                  beforeStartDateValue: data.beforeStartDateValue,
                });
              }}
              onChange={setRepeatsData}
            />
          )}
      </form>
    </Card>
  );
};
